export type FeatureFlagDiffEntry =
  | { kind: 'allAdvisors-set'; flagKey: string; previous: boolean | undefined; next: boolean }
  | { kind: 'allAdvisors-remove'; flagKey: string; previous: boolean }
  | {
      kind: 'advisor-set';
      advisorId: string;
      flagKey: string;
      previous: boolean | undefined;
      next: boolean;
    }
  | { kind: 'advisor-remove'; advisorId: string; flagKey: string; previous: boolean }
  | { kind: 'advisor-section-remove'; advisorId: string }
  | {
      kind: 'client-set';
      clientId: string;
      flagKey: string;
      previous: boolean | undefined;
      next: boolean;
    };

export type FeatureFlagDiff = {
  entries: FeatureFlagDiffEntry[];
  hasChanges: boolean;
};

function getBool(
  record: Record<string, unknown> | undefined,
  key: string,
): boolean | undefined {
  const v = record?.[key];
  return typeof v === 'boolean' ? v : undefined;
}

/**
 * Structured diff between two AppConfig snapshots.
 */
export function computeFeatureFlagDiff(
  previous: AppConfigSchema,
  next: AppConfigSchema,
): FeatureFlagDiff {
  const entries: FeatureFlagDiffEntry[] = [];

  const prevAll = (previous.allAdvisors ?? {}) as Record<string, unknown>;
  const nextAll = (next.allAdvisors ?? {}) as Record<string, unknown>;
  const allKeys = new Set([...Object.keys(prevAll), ...Object.keys(nextAll)]);

  for (const key of [...allKeys].sort()) {
    const prev = getBool(prevAll, key);
    const nxt = getBool(nextAll, key);
    if (prev === nxt) continue;

    if (nxt === undefined && prev !== undefined) {
      entries.push({ kind: 'allAdvisors-remove', flagKey: key, previous: prev });
    } else if (nxt !== undefined) {
      entries.push({ kind: 'allAdvisors-set', flagKey: key, previous: prev, next: nxt });
    }
  }

  const prevAdvisors = previous.advisors ?? {};
  const nextAdvisors = next.advisors ?? {};
  const advisorIds = new Set([
    ...Object.keys(prevAdvisors),
    ...Object.keys(nextAdvisors),
  ]);

  for (const advisorId of [...advisorIds].sort()) {
    const prevSection = prevAdvisors[advisorId] as Record<string, unknown> | undefined;
    const nextSection = nextAdvisors[advisorId] as Record<string, unknown> | undefined;

    if (prevSection && !nextSection) {
      entries.push({ kind: 'advisor-section-remove', advisorId });
      continue;
    }

    const sectionKeys = new Set([
      ...Object.keys(prevSection ?? {}),
      ...Object.keys(nextSection ?? {}),
    ]);

    for (const key of [...sectionKeys].sort()) {
      const prev = getBool(prevSection, key);
      const nxt = getBool(nextSection, key);
      if (prev === nxt) continue;

      if (nxt === undefined && prev !== undefined) {
        entries.push({ kind: 'advisor-remove', advisorId, flagKey: key, previous: prev });
      } else if (nxt !== undefined) {
        entries.push({
          kind: 'advisor-set',
          advisorId,
          flagKey: key,
          previous: prev,
          next: nxt,
        });
      }
    }
  }

  const prevClients = previous.clients ?? {};
  const nextClients = next.clients ?? {};
  const clientIds = new Set([...Object.keys(prevClients), ...Object.keys(nextClients)]);

  for (const clientId of [...clientIds].sort()) {
    const prevSection = prevClients[clientId] as Record<string, unknown> | undefined;
    const nextSection = nextClients[clientId] as Record<string, unknown> | undefined;
    const sectionKeys = new Set([
      ...Object.keys(prevSection ?? {}),
      ...Object.keys(nextSection ?? {}),
    ]);
    for (const key of [...sectionKeys].sort()) {
      const prev = getBool(prevSection, key);
      const nxt = getBool(nextSection, key);
      if (prev === nxt) continue;
      if (nxt !== undefined) {
        entries.push({ kind: 'client-set', clientId, flagKey: key, previous: prev, next: nxt });
      }
    }
  }

  return { entries, hasChanges: entries.length > 0 };
}
